import React from 'react'
import { animated } from '@react-spring/web'
import { styled } from 'styled-components'
import type { AbstractComponent } from 'react'
import { ONE_HOUR, ONE_MINUTE, ONE_SECOND } from '../../../utility/constants'
import { PERCENTAGE_OF_SCREEN_TAKEN_BY_BOTTOM_FRAME } from '../../frames/MissionFrame/BottomFrame'
import {
	STATIC_BACKGROUND_URL,
	BACKGROUND_VIDEO_URL_NO_LOGO,
} from '../../../assets/constants'
import { BackgroundVideo } from '../../frames/BasicFrame'

export const PlayerWrapper: AbstractComponent<
	$Shape<{| children?: React$Node, css?: string |}>
> = styled.div`
	text-align: center;
	color: white;
	width: 100%;
	height: 100%;
	background-image: url(${STATIC_BACKGROUND_URL});
`

export const Header: AbstractComponent<{|
	children?: React$Node,
|}> = styled.div.attrs({ className: 'text-3xl' })`
	${({ theme }) => `
		font-weight: ${theme.fontSemiBold};	
		background-color: ${theme.base3};
	`}
	width: 100%;
	text-align: center;
`

export const MarkdownHeader: AbstractComponent<{|
	children?: React$Node,
	className?: string,
	style?: { [string]: string },
|}> = styled(Header)`
	text-align: left;
	display: flex;
	justify-content: center;
`

export function StudentsQuestionDisplay({
	students,
}: {
	students: Array<{ id: string, fullName: string, submitted: boolean }>,
}): React$Node {
	return (
		<StudentsWrapper>
			{students.map(student => (
				<Student key={student.id} submitted={student.submitted}>
					{student.fullName}
				</Student>
			))}
		</StudentsWrapper>
	)
}

const Student = styled.div`
	${({ theme, submitted }) => `
		padding: ${theme.spacing} ${theme.spacing2x};
		background-color: ${submitted ? theme.highlight : theme.base3};
		color: ${submitted ? theme.base3 : theme.highlight};
		margin: 4px 4px;
		font-family: ${theme.fontFamily};
	`}
	border-radius: 4px;
`

const StudentsWrapper = styled.div`
	display: flex;
	flex-flow: row wrap;
	width: 90%;
	margin: auto;
`

export const Image: AbstractComponent<{| src?: ?string |}> = styled.img`
	max-width: 20%;
	max-height: 20%;
	margin: auto;
`

/**
 * The header used for culminating moment and collaborative culminating moment screens. Provides 3 slots:
 * 1. Left slot for timer
 * 2. Center slot for the main content
 * 3. Right slot for additional content
 * @param {number} timeLeft - the time left on the timer
 * @param {boolean} hideTimer - whether or not to hide the timer
 * @param {React$Node} children - the main content
 * @param {React$Node} rightChild - the additional content
 */
export function CulminatingMomentHeader({
	timeLeft,
	hideTimer,
	children,
	className,
	style,
	timerStyles,
	rightChild,
}: {
	className?: string,
	timeLeft?: number,
	hideTimer?: boolean,
	timerStyles?: { [string]: string | number },
	style?: { [string]: string | number },
	children: React$Node,
	rightChild?: React$Node,
}): React$Node {
	return (
		<StyledHeader className={[className, 'pt-2 pr-4'].join(' ')} style={style}>
			<TimerText
				style={timerStyles}
				$isTransparent={hideTimer || !timeLeft}
				className="font-game-headline">
				{timeLeft !== undefined ? formatAsTime(timeLeft) : ''}
			</TimerText>
			<HeaderCenterContent>{children}</HeaderCenterContent>
			<HeaderRightContent
				className="font-xs font-game-headline"
				$isTransparent={!rightChild}>
				{rightChild}
			</HeaderRightContent>
		</StyledHeader>
	)
}

const HeaderRightContent = styled.div`
	min-width: 10%;
	flex: 0 0 auto;
	margin: ${({ theme }) => theme.spacing};
	color: ${({ $isTransparent }) =>
		$isTransparent ? 'transparent' : 'inherit'};
	display: flex;
	justify-content: center;
	align-items: center;
	transition: color 1s;
	font-weight: ${({ theme }) => theme.fontLight};
`

const StyledHeader = styled(Header)`
	display: flex;
	align-items: center;
`

const HeaderCenterContent = styled.h1`
	flex: 1 1 0;
	margin: 0;
`

/**
 * Formats the given milliseconds into a time string of the format hh:MM:SS
 * @param {number} milliseconds - the milliseconds to format
 */
function formatAsTime(milliseconds: number): string {
	const hours = Math.floor(milliseconds / ONE_HOUR)
	const minutes = Math.floor((milliseconds % ONE_HOUR) / ONE_MINUTE)
	const seconds = Math.floor((milliseconds % ONE_MINUTE) / ONE_SECOND)
	return `${hours ? `${hours}:` : ''}${
		hours || minutes ? `${minutes.toString().padStart(hours ? 2 : 1, '0')}` : ''
	}:${seconds.toString().padStart(2, '0')}`
}

const TimerText = animated(styled.div`
	color: ${({ theme, $isTransparent }) =>
		$isTransparent ? 'Transparent' : 'inherit'};
	transition: color 1s;
	min-width: 10%;
	flex: 0 0 auto;
	margin: ${({ theme }) => theme.spacing};
	display: flex;
	justify-content: center;
	align-items: center;
`)

/**
 * The frame footer takes up a fair amount of the screen, and in some views we want to make sure that the content is not covered.
 * This can be placed below the content to move it above the footer
 */
export const MissionFrameFooterSpacer: AbstractComponent<{}> = styled.div`
	height: ${PERCENTAGE_OF_SCREEN_TAKEN_BY_BOTTOM_FRAME}%;
	min-height: ${PERCENTAGE_OF_SCREEN_TAKEN_BY_BOTTOM_FRAME}%;
`

/**
 * The wrapper used for different steps during the collaborative culminating moment screen
 * @returns {React$Node}
 */
export function CollaborativeCulminatingMomentWrapper({
	children,
}: {
	children: React$Node,
}): React$Node {
	return (
		<div className="flex flex-col h-full ">
			<BackgroundVideo src={BACKGROUND_VIDEO_URL_NO_LOGO} />
			{children}
			<MissionFrameFooterSpacer />
		</div>
	)
}
